import _ from "lodash";
import moment, {Moment} from 'moment';
import {cacheTTL2nextRefresh} from 'util/cacheTTL2nextRefresh';

export type TTL = string | null
type IdPart = string | number
type IdPath = IdPart | IdPart[]

interface CacheModel<T> {
	data: T,
	cacheTTL: TTL,
	timestamp: Moment,
	refreshAt?: Moment,
}

export class CacheCollection<T> {
	public defaultTTL: TTL;
	private cache = {};

	constructor(defaultTTL?: TTL) {
		if (defaultTTL != null) this.defaultTTL = defaultTTL;
	}

	getCache(idPath: IdPath): T | undefined | null {
		let model = _.get(this.cache, idPath);
		if (this.needsRefresh(model)) {
			return undefined;
		} else {
			let data = model && model.data;
			return data === undefined ? null : data;
		}
	}

	/**
	 *
	 * @param idPath {Array<String|number>|string|number} index path (Array is resolved to path, String is not)
	 * @param data {Promise|*}
	 * @param [ttl] optional ttl string, will be overridden by data if present
	 * @returns {*}
	 */
	setCache(idPath: IdPath, data: T, ttl?: TTL) {
		if (!_.isArray(idPath)) idPath = [idPath];
		const model = this.createModel(data, moment());
		if (ttl !== undefined) model.cacheTTL = ttl;
		else if (this.defaultTTL != null) model.cacheTTL = this.defaultTTL;
		this.refreshCacheTTL(model);
		_.set(this.cache, idPath, model);
		return data;
	}

	removeCache = (idPath: IdPath, strictEqualValue?: T) => {
		if (typeof idPath === 'string') idPath = [idPath];
		if (strictEqualValue === undefined || _.get(this.cache, idPath) === strictEqualValue) _.set(this.cache, idPath, undefined);
	}

	clearCache = () => {
		this.cache = {};
	}

	private createModel(data: T, timestamp: Moment) {
		return {
			data,
			timestamp,
			cacheTTL: '00:00 PT1H',
			refreshAt: undefined,
		} as CacheModel<T>
	}

	private refreshCacheTTL(model: CacheModel<T>, serverResp?: any) {
		if (!model) return;
		const {data} = serverResp || model.data;
		const isPromise = data && typeof data.then === 'function';
		if (isPromise) {
			// set asynchronous (data is promise or deferred)
			data.then((serverResp: any) => this.refreshCacheTTL(model, serverResp));
			return;
		}

		//set synchronous
		if (_.has(data, "cacheTTL")) {
			model.cacheTTL = data.cacheTTL
		}
		this.generateRefreshAt(model);
	}

	private needsRefresh(model: CacheModel<T>) {
		if (!model) return true;
		const rt = model.refreshAt;
		// do only refresh if refreshAt is set properly and is before the current timestamp
		return rt && rt.isBefore(moment());
	}

	private generateRefreshAt(model: CacheModel<T>) {
		if (!model) return;
		let {timestamp, cacheTTL} = model;
		const refreshAt = cacheTTL2nextRefresh(cacheTTL, timestamp)
		if (refreshAt && moment.isMoment(refreshAt) && refreshAt.isValid()) model.refreshAt = refreshAt;
	}
}

export default CacheCollection;